import Spinner from "../../../components/Spinners";
import Button from "../../../components/Button";
import { Tooltip } from "../../../components/tooltip";
import { useDiagnostics } from "../hooks";
import { getThresholdStyle, getThresholdCoverageStyle } from "../utils";
import {
  DiagnosticsSnapshot,
  DiagnosticsHotelResult,
  DiagnosticsPriceCoverage,
  PriceCoverageBucket,
  PriceAccuracyScore,
} from "../types";
import { Badge, BooleanBadge } from "./StatusBadge";

const SCORE_STYLES: Record<PriceAccuracyScore, { label: string; className: string }> = {
  EXCELLENT: { label: "Excellent", className: "bg-green-100 text-green-800 border-green-300" },
  GOOD: { label: "Fair", className: "bg-green-100 text-green-800 border-green-300" },
  FAIR: { label: "Fair", className: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  POOR: { label: "Poor", className: "bg-red-100 text-red-800 border-red-300" },
};

function ScoreBadge({ score }: { score: PriceAccuracyScore | null }) {
  if (!score) return <span className="text-sm text-gray-400">&mdash;</span>;
  const style = SCORE_STYLES[score] || { label: score, className: "bg-gray-100 text-gray-800 border-gray-300" };
  return (
    <Badge className={`px-2.5 font-semibold ${style.className}`}>
      {style.label}
    </Badge>
  );
}

function ParticipationBadge({ percent }: { percent: number | null }) {
  if (percent == null)
    return <span className="text-sm text-gray-400">&mdash;</span>;
  return (
    <Badge className={`px-2.5 font-semibold ${getThresholdStyle(percent)}`}>
      {percent.toFixed(1)}%
    </Badge>
  );
}

function formatMatchStatus(status: string): string {
  return status
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const MATCH_STYLES: Record<string, string> = {
  MATCHED: "bg-green-100 text-green-800 border-green-300",
  NOT_MATCHED: "bg-red-100 text-red-800 border-red-300",
  NOT_FOUND_IN_HOTEL_CENTER: "bg-red-100 text-red-800 border-red-300",
  UNKNOWN: "bg-gray-100 text-gray-800 border-gray-300",
};

const BOOKING_WINDOW_LABELS: Record<string, string> = {
  DAYS_0_TO_30: "0\u201330 days",
  DAYS_31_TO_60: "31\u201360 days",
  DAYS_61_TO_90: "61\u201390 days",
  DAYS_91_TO_120: "91\u2013120 days",
  DAYS_121_TO_150: "121\u2013150 days",
  DAYS_151_TO_180: "151\u2013180 days",
  DAYS_181_TO_210: "181\u2013210 days",
  DAYS_211_TO_240: "211\u2013240 days",
  DAYS_241_TO_270: "241\u2013270 days",
  DAYS_271_TO_300: "271\u2013300 days",
  DAYS_301_TO_330: "301\u2013330 days",
};

const LOS_LABELS: Record<string, string> = {
  LENGTH_OF_STAY_1_TO_7: "1\u20137 nights",
  LENGTH_OF_STAY_8_TO_14: "8\u201314 nights",
  LENGTH_OF_STAY_15_TO_30: "15\u201330 nights",
};

function CoverageCell({ percent }: { percent: number }) {
  return (
    <span
      className={`inline-flex items-center justify-center px-2 py-1 rounded text-xs font-semibold ${getThresholdCoverageStyle(percent)}`}
    >
      {percent.toFixed(0)}%
    </span>
  );
}

function CoverageTable({ buckets }: { buckets: PriceCoverageBucket[] }) {
  const losKeys = Array.from(
    new Set(buckets.map((b) => b.lengthOfStayRange))
  );
  const windowKeys = Array.from(
    new Set(buckets.map((b) => b.advanceBookingWindowRange))
  );

  const lookup = new Map<string, number>();
  for (const b of buckets) {
    lookup.set(
      `${b.advanceBookingWindowRange}|${b.lengthOfStayRange}`,
      b.priceCoveragePercent
    );
  }

  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#F4F4F6]">
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase rounded-tl-lg rounded-bl-lg">
              Booking Window
            </th>
            {losKeys.map((los, i) => (
              <th
                key={los}
                className={`px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase ${
                  i === losKeys.length - 1 ? "rounded-tr-lg rounded-br-lg" : ""
                }`}
              >
                {LOS_LABELS[los] || los}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {windowKeys.map((window) => (
            <tr key={window} className="border-b border-b-[#E9E9EB]">
              <td className="px-3 py-2.5 text-xs text-gray-700 font-medium">
                {BOOKING_WINDOW_LABELS[window] || window}
              </td>
              {losKeys.map((los) => {
                const pct = lookup.get(`${window}|${los}`);
                return (
                  <td key={los} className="px-3 py-2.5 text-center">
                    {pct !== undefined ? (
                      <CoverageCell percent={pct} />
                    ) : (
                      <span className="text-gray-300">&mdash;</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PriceCoverageCard({
  coverage,
}: {
  coverage: DiagnosticsPriceCoverage;
}) {
  const overallPercent = coverage.priceCoveragePercent ?? 0;
  const binaryPercent = coverage.priceCoverageBinaryPercent ?? 0;
  const buckets = coverage.priceCoverageBuckets || [];
  const calcDate = coverage.calculationDate;
  const dateStr = calcDate
    ? `${calcDate.month}/${calcDate.day}/${calcDate.year}`
    : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border border-[#E9E9EB]">
          <p className="text-xs text-gray-500 mb-1">Overall Coverage</p>
          <p className="text-2xl font-semibold">{overallPercent.toFixed(1)}%</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-[#E9E9EB]">
          <p className="text-xs text-gray-500 mb-1">Hotels With Prices</p>
          <p className="text-2xl font-semibold">{binaryPercent.toFixed(0)}%</p>
        </div>
      </div>

      {coverage.matchedPropertyCount !== undefined && (
        <p className="text-xs text-gray-500 text-center">
          {coverage.matchedPropertyCount} matched{" "}
          {coverage.matchedPropertyCount === 1 ? "property" : "properties"}
          {dateStr && <> &mdash; as of {dateStr}</>}
        </p>
      )}

      {buckets.length > 0 && <CoverageTable buckets={buckets} />}
    </div>
  );
}

function HotelResultsTable({
  hotels,
}: {
  hotels: DiagnosticsHotelResult[];
}) {
  if (hotels.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-4 text-center">
        No hotel data available yet.
      </p>
    );
  }

  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#F4F4F6]">
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase rounded-tl-lg rounded-bl-lg">
              Hotel
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Match
            </th>
            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
              Live
            </th>
            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
              Participation
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase rounded-tr-lg rounded-br-lg">
              Issues
            </th>
          </tr>
        </thead>
        <tbody>
          {hotels.map((hotel, idx) => {
            const matchStyle =
              MATCH_STYLES[hotel.matchStatus] ||
              "bg-gray-100 text-gray-800 border-gray-300";
            const allIssues = [
              ...(hotel.matchFailureReasons || []),
              ...hotel.dataIssues,
            ];

            return (
              <tr key={idx} className="border-b border-[#E9E9EB]">
                <td className="px-3 py-2.5">
                  <span className="font-medium text-gray-900">
                    {hotel.hotelName}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <Badge className={matchStyle}>
                    {formatMatchStatus(hotel.matchStatus)}
                  </Badge>
                </td>
                <td className="px-3 py-2.5 text-center">
                  <BooleanBadge value={hotel.liveOnGoogle} />
                </td>
                <td className="px-3 py-2.5 text-center">
                  <ParticipationBadge percent={hotel.participationPercent} />
                </td>
                <td className="px-3 py-2.5">
                  {allIssues.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {allIssues.map((issue, i) => (
                        <Badge key={i} className="bg-yellow-100 text-yellow-800 border-yellow-300">
                          {issue}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <Badge className="bg-green-100 text-green-800 border-green-300">
                      None
                    </Badge>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MissedParticipationDetail({
  hotel,
}: {
  hotel: DiagnosticsHotelResult;
}) {
  const mp = hotel.missedParticipation;
  if (!mp || !hotel.missedParticipationCount) return null;

  const topLevel = [
    { label: "Price problems", count: mp.priceProblemCount },
    { label: "No price", count: mp.noPriceCount },
    { label: "Price missing", count: mp.priceMissingCount },
    { label: "Price unavailable", count: mp.priceUnavailableCount },
    { label: "No landing page", count: mp.noLandingPageCount },
    { label: "Other", count: mp.otherReasonCount },
  ].filter((i) => i.count > 0);

  const noPriceDetails = mp.noPriceCountDetails;
  const priceMissingDetails = mp.priceMissingCountDetails;

  const subItems = [
    ...(noPriceDetails
      ? [
          { label: "Live pricing not available", count: noPriceDetails.livePricingNotAvailableCount },
          { label: "Tax config issue", count: noPriceDetails.livePricingTaxConfigIssueCount },
          { label: "Live pricing other", count: noPriceDetails.livePricingOtherReasonCount },
        ]
      : []),
    ...(priceMissingDetails
      ? [
          { label: "Cache rate missing", count: priceMissingDetails.cacheRateMissingCount },
          { label: "Itinerary blocked", count: priceMissingDetails.itineraryBlockedCount },
        ]
      : []),
  ].filter((i) => i.count > 0);

  if (topLevel.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-5">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-base font-semibold text-gray-900">
          Missed Participation &mdash; {hotel.hotelName}
        </h3>
        <Tooltip id={`missed-${hotel.googlePropertyId}`} className="!w-[260px]">
          <p className="text-xs">
            Breakdown of why {hotel.missedParticipationCount} search
            opportunities were missed out of {hotel.opportunityCount} total.
          </p>
        </Tooltip>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {topLevel.map((item) => (
          <div
            key={item.label}
            className="bg-white rounded-lg p-3 shadow-sm border border-[#E9E9EB]"
          >
            <p className="text-xs text-gray-500 mb-1">{item.label}</p>
            <p className="text-lg font-semibold">{item.count}</p>
          </div>
        ))}
      </div>

      {subItems.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#F0F0F2]">
          <p className="text-xs text-gray-500 mb-2">Details</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {subItems.map((item) => (
              <div
                key={item.label}
                className="bg-[#FAFAFA] rounded-lg p-3 border border-[#E9E9EB]"
              >
                <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                <p className="text-base font-semibold">{item.count}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function DiagnosticsContent({
  googlePropertyId,
}: {
  googlePropertyId?: string;
}) {
  const { data, isLoading, isError, refetch } = useDiagnostics();

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="text-black" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-12 text-center">
        <div className="text-3xl mb-3 text-gray-300">!</div>
        <p className="text-gray-600 font-medium mb-1">
          Couldn't load diagnostics
        </p>
        <p className="text-sm text-gray-400 mb-4">
          There was a problem fetching diagnostics data.
        </p>
        <Button variant="outline" onClick={() => refetch()}>
          Try Again
        </Button>
      </div>
    );
  }

  const snapshots: DiagnosticsSnapshot[] = Array.isArray(data) ? data : [];
  const latest = snapshots[0];

  if (!latest) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-12 text-center">
        <div className="text-3xl mb-3 text-gray-300">~</div>
        <p className="text-gray-600 font-medium mb-1">No diagnostics yet</p>
        <p className="text-sm text-gray-400">
          Run the "Hotel Center Diagnostics" job from the Operations tab to
          generate the first report.
        </p>
      </div>
    );
  }

  const hotels = googlePropertyId
    ? latest.hotelResults.filter(
        (h) => h.googlePropertyId === googlePropertyId
      )
    : latest.hotelResults;

  const issueCount = hotels.reduce(
    (sum, h) =>
      sum + h.dataIssues.length + (h.matchFailureReasons?.length || 0),
    0
  );

  const hotelsWithMissedParticipation = hotels.filter(
    (h) =>
      h.missedParticipation &&
      h.missedParticipationCount &&
      h.missedParticipationCount > 0
  );

  return (
    <div className="space-y-5">
      {!googlePropertyId && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 shadow-sm border border-[#E9E9EB]">
            <p className="text-xs text-gray-500 mb-1">Total Configs</p>
            <p className="text-2xl font-semibold">{latest.totalConfigs}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-[#E9E9EB]">
            <p className="text-xs text-gray-500 mb-1">Price Accuracy</p>
            <ScoreBadge
              score={latest.priceAccuracy?.currentOverallScore ?? null}
            />
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-[#E9E9EB]">
            <p className="text-xs text-gray-500 mb-1">Avg. Participation</p>
            <ParticipationBadge
              percent={latest.averageParticipationPercent}
            />
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-[#E9E9EB]">
            <p className="text-xs text-gray-500 mb-1">Total Issues</p>
            <p
              className={`text-2xl font-semibold ${issueCount > 0 ? "text-red-600" : "text-green-600"}`}
            >
              {issueCount}
            </p>
          </div>
        </div>
      )}

      {latest.priceCoverage && (
        <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-5">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-base font-semibold text-gray-900">
              Price Coverage
            </h3>
            <Tooltip id="price-coverage-info" className="!w-[260px]">
              <p className="text-xs">
                The percentage of traveler searches where your hotels show a
                price. Higher is better &mdash; aim for 90%+.
              </p>
            </Tooltip>
          </div>
          <PriceCoverageCard coverage={latest.priceCoverage} />
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-5">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-base font-semibold text-gray-900">
            {googlePropertyId ? "Hotel Status" : "Hotel Results"}
          </h3>
          <Tooltip id="hotel-results-info" className="!w-[260px]">
            <p className="text-xs">
              {googlePropertyId
                ? "Match status, live status, participation, and any data issues for this hotel."
                : "Per-hotel match status, live status, participation rates, and data issues from Google Hotel Center."}
            </p>
          </Tooltip>
        </div>
        <HotelResultsTable hotels={hotels} />
      </div>

      {hotelsWithMissedParticipation.map((hotel) => (
        <MissedParticipationDetail
          key={hotel.googlePropertyId}
          hotel={hotel}
        />
      ))}
    </div>
  );
}
